import { Colonia } from '../../types/colonia';

const THUMB_ALTURA = 96; // px na planilha
const PARALELISMO = 6;
const TOTAL_COLUNAS = 12;
const LINHA_CABECALHO = 3;

type Thumb = { base64: string; width: number; height: number };

const CORES = {
  azul: 'FF3398D3',
  azulEscuro: 'FF1F6F9F',
  zebra: 'FFF3F7FA',
  borda: 'FFCBD5E1',
  cinza: 'FF64748B',
  verdeFundo: 'FFDCFCE7',
  verdeTexto: 'FF15803D',
  laranjaFundo: 'FFFEF3C7',
  laranjaTexto: 'FFB45309',
  vermelhoFundo: 'FFFEE2E2',
  vermelhoTexto: 'FFB91C1C'
};

/** Baixa a foto e reduz para miniatura — sem isso a planilha ficaria com dezenas de MB. */
const carregarThumb = async (url: string): Promise<Thumb | null> => {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('foto_indisponivel'));
      img.src = url;
    });
    const escala = THUMB_ALTURA / Math.max(1, img.naturalHeight);
    const largura = Math.max(1, Math.round(img.naturalWidth * escala));
    const canvas = document.createElement('canvas');
    canvas.width = largura;
    canvas.height = THUMB_ALTURA;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, largura, THUMB_ALTURA);
    return { base64: canvas.toDataURL('image/jpeg', 0.8), width: largura, height: THUMB_ALTURA };
  } catch {
    return null; // sem CORS ou foto quebrada: a linha sai sem imagem
  }
};

const corDoStatus = (status: string) => {
  const texto = String(status || '').toLowerCase();
  if (texto === 'confirmado') return { fundo: CORES.verdeFundo, texto: CORES.verdeTexto };
  if (texto === 'cancelado') return { fundo: CORES.vermelhoFundo, texto: CORES.vermelhoTexto };
  return { fundo: CORES.laranjaFundo, texto: CORES.laranjaTexto };
};

export const exportarInscricoesExcel = async (
  inscricoes: Colonia[],
  ano: string,
  onProgress?: (feitas: number, total: number) => void
) => {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(`Inscrições ${ano}`.slice(0, 31));

  sheet.columns = [
    { key: 'foto', width: 15 },
    { key: 'nome', width: 34 },
    { key: 'idade', width: 8 },
    { key: 'turma', width: 14 },
    { key: 'camisa', width: 10 },
    { key: 'nascimento', width: 14 },
    { key: 'responsavel', width: 30 },
    { key: 'whatsapp', width: 18 },
    { key: 'status', width: 13 },
    { key: 'pago', width: 8 },
    { key: 'gratuito', width: 10 },
    { key: 'valor', width: 14 }
  ] as never;

  // Título e subtítulo
  sheet.mergeCells(1, 1, 1, TOTAL_COLUNAS);
  const titulo = sheet.getCell(1, 1);
  titulo.value = 'Colônia de Férias UBA Clube — Inscrições';
  titulo.font = { bold: true, size: 16, color: { argb: CORES.azulEscuro } };
  titulo.alignment = { vertical: 'middle', horizontal: 'center' };
  sheet.getRow(1).height = 30;

  sheet.mergeCells(2, 1, 2, TOTAL_COLUNAS);
  const geradoEm = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date());
  const subtitulo = sheet.getCell(2, 1);
  subtitulo.value = `Período ${ano} • ${inscricoes.length} inscrições • gerado em ${geradoEm}`;
  subtitulo.font = { size: 11, color: { argb: CORES.cinza } };
  subtitulo.alignment = { vertical: 'middle', horizontal: 'center' };
  sheet.getRow(2).height = 18;

  // Cabeçalho
  const cabecalho = sheet.getRow(LINHA_CABECALHO);
  cabecalho.values = [
    'Foto',
    'Nome da Criança',
    'Idade',
    'Turma',
    'Camisa',
    'Nascimento',
    'Responsável',
    'WhatsApp',
    'Status',
    'Pago',
    'Gratuito',
    'Valor Total'
  ];
  cabecalho.height = 26;
  cabecalho.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CORES.azul } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = {
      top: { style: 'thin', color: { argb: CORES.borda } },
      bottom: { style: 'thin', color: { argb: CORES.borda } },
      left: { style: 'thin', color: { argb: CORES.borda } },
      right: { style: 'thin', color: { argb: CORES.borda } }
    };
  });
  sheet.views = [{ state: 'frozen', ySplit: LINHA_CABECALHO }];

  // Miniaturas com paralelismo limitado para não saturar a rede
  const thumbs: Array<Thumb | null> = new Array(inscricoes.length).fill(null);
  let proxima = 0;
  let feitas = 0;
  const baixar = async () => {
    while (proxima < inscricoes.length) {
      const indice = proxima;
      proxima += 1;
      const url = inscricoes[indice].fotoUrl;
      thumbs[indice] = url ? await carregarThumb(url) : null;
      feitas += 1;
      onProgress?.(feitas, inscricoes.length);
    }
  };
  await Promise.all(Array.from({ length: PARALELISMO }, baixar));

  inscricoes.forEach((inscricao, indice) => {
    const row = sheet.addRow({
      foto: '',
      nome: inscricao.nomeCompleto,
      idade: inscricao.idade || '',
      turma: inscricao.turma || 'Sem turma',
      camisa: inscricao.tamanhoCamisa || '',
      nascimento: inscricao.dataNascimento || '',
      responsavel: inscricao.nomeMae || '',
      whatsapp: inscricao.telefoneMae || '',
      status: inscricao.status || '',
      pago: inscricao.pago ? 'Sim' : 'Não',
      gratuito: inscricao.gratuito ? 'Sim' : 'Não',
      valor: Number(inscricao.valorTotal) || 0
    });
    row.height = 76; // pontos (~96px), altura da miniatura

    const zebra = indice % 2 === 1;
    row.eachCell({ includeEmpty: true }, (cell, coluna) => {
      cell.border = {
        top: { style: 'thin', color: { argb: CORES.borda } },
        bottom: { style: 'thin', color: { argb: CORES.borda } },
        left: { style: 'thin', color: { argb: CORES.borda } },
        right: { style: 'thin', color: { argb: CORES.borda } }
      };
      if (zebra) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CORES.zebra } };
      }
      // Nome e responsável à esquerda; números e etiquetas centralizados; valor à direita
      if (coluna === 2 || coluna === 7) {
        cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      } else if (coluna === TOTAL_COLUNAS) {
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      } else {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      }
    });

    row.getCell('nome').font = { bold: true, size: 11 };
    row.getCell('valor').numFmt = '"R$" #,##0.00';

    const status = corDoStatus(inscricao.status || '');
    const statusCell = row.getCell('status');
    statusCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: status.fundo } };
    statusCell.font = { bold: true, color: { argb: status.texto }, size: 10 };

    const pagoCell = row.getCell('pago');
    pagoCell.font = { bold: true, color: { argb: inscricao.pago ? CORES.verdeTexto : CORES.vermelhoTexto } };

    const thumb = thumbs[indice];
    if (thumb) {
      const imagemId = workbook.addImage({ base64: thumb.base64, extension: 'jpeg' });
      sheet.addImage(imagemId, {
        tl: { col: 0.15, row: LINHA_CABECALHO + indice + 0.05 },
        ext: { width: Math.min(thumb.width, 96), height: thumb.height },
        editAs: 'oneCell'
      });
    }
  });

  // Filtro automático no cabeçalho, cobrindo os dados
  const ultimaLinhaDados = LINHA_CABECALHO + inscricoes.length;
  sheet.autoFilter = {
    from: { row: LINHA_CABECALHO, column: 1 },
    to: { row: ultimaLinhaDados, column: TOTAL_COLUNAS }
  };

  // Linha de totais
  const pagas = inscricoes.filter((item) => item.pago).length;
  const totalValor = inscricoes.reduce((soma, item) => soma + (Number(item.valorTotal) || 0), 0);
  const linhaTotal = ultimaLinhaDados + 1;
  sheet.mergeCells(linhaTotal, 1, linhaTotal, TOTAL_COLUNAS - 1);
  const rotuloTotal = sheet.getCell(linhaTotal, 1);
  rotuloTotal.value = `Total: ${inscricoes.length} inscrições • ${pagas} pagas • ${inscricoes.length - pagas} não pagas`;
  rotuloTotal.font = { bold: true, size: 11 };
  rotuloTotal.alignment = { vertical: 'middle', horizontal: 'right' };
  const valorTotalCell = sheet.getCell(linhaTotal, TOTAL_COLUNAS);
  valorTotalCell.value = totalValor;
  valorTotalCell.numFmt = '"R$" #,##0.00';
  valorTotalCell.font = { bold: true, size: 11, color: { argb: CORES.azulEscuro } };
  valorTotalCell.alignment = { vertical: 'middle', horizontal: 'right' };
  const linhaTotalRow = sheet.getRow(linhaTotal);
  linhaTotalRow.height = 24;
  linhaTotalRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.border = { top: { style: 'double', color: { argb: CORES.azul } } };
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `inscricoes-${ano}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
